import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import { ModelSelector, type ModelSelectorValue } from "../agent/ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback, type ModelFallbackJson } from "../agent/ModelFallbackSection";

interface SubAgent {
  id: string;
  key: string;
  name: string;
  prompt: string;
  model_provider: string;
  model_name: string;
  temperature: number;
  reasoning_effort: string | null;
  model_fallback?: ModelFallbackJson | null;
  tools: any[];
}


const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

export function SubAgentsTab() {
  const { data: agents = [] } = useQuery<SubAgent[]>({
    queryKey: ["dev-subagents"],
    queryFn: () => api.get("/dev/subagents"),
  });
  const [selected, setSelected] = useState<SubAgent | null>(null);

  return (
    <div style={{ display: "flex", gap: 20, minHeight: 400 }}>
      {/* Sidebar list */}
      <div style={{ width: 220, flexShrink: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>Sub-agentes</span>
        {agents.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelected(s)}
            style={{
              textAlign: "left",
              padding: "9px 12px",
              borderRadius: 8,
              border: `1px solid ${selected?.id === s.id ? "var(--brand)" : "var(--line)"}`,
              background: selected?.id === s.id ? "var(--brand-soft)" : "var(--surface-hover)",
              color: selected?.id === s.id ? "var(--brand)" : "var(--ink-strong)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: 500 }}>{s.name}</div>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 2 }}>{s.key}</div>
          </button>
        ))}
      </div>

      {/* Editor */}
      <div style={{ flex: 1 }}>
        {selected
          ? <SubAgentEditor sub={selected} key={selected.id} onSaved={(updated) => setSelected(updated)} />
          : <div style={{ color: "var(--ink-dim)", fontSize: 13, paddingTop: 40 }}>← Selecione um sub-agente</div>
        }
      </div>
    </div>
  );
}

function SubAgentEditor({ sub, onSaved }: { sub: SubAgent; onSaved: (s: SubAgent) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    prompt: sub.prompt,
    model_provider: sub.model_provider,
    model_name: sub.model_name,
    temperature: sub.temperature,
    reasoning_effort: sub.reasoning_effort ?? "off",
  });
  const [fallback, setFallback] = useState<ModelSelectorValue | null>(fallbackToForm(sub.model_fallback));
  const [saved, setSaved] = useState(false);

  const mut = useMutation({
    mutationFn: () => api.patch(`/dev/subagents/${sub.id}`, {
      ...form,
      reasoning_effort: form.reasoning_effort === "off" ? null : form.reasoning_effort,
      model_fallback: formToFallback(fallback),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dev-subagents"] });
      onSaved({
        ...sub, ...form,
        reasoning_effort: form.reasoning_effort === "off" ? null : form.reasoning_effort,
        model_fallback: formToFallback(fallback),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{sub.name}</span>
        <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 8 }}>{sub.key}</span>
      </div>

      {/* Prompt */}
      <div>
        <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Prompt do sistema</label>
        <textarea
          style={{ ...inp, width: "100%", height: 220, resize: "vertical", fontFamily: "var(--font-mono)", fontSize: 12 }}
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
        />
      </div>

      {/* Modelo + temperatura + raciocínio (mesmo seletor do agente) */}
      <ModelSelector
        compact
        showMaxTokens={false}
        value={{ provider: form.model_provider, model_name: form.model_name, temperature: form.temperature, reasoning_effort: form.reasoning_effort }}
        onChange={(next) => setForm({
          ...form, model_provider: next.provider, model_name: next.model_name,
          temperature: next.temperature, reasoning_effort: next.reasoning_effort,
        })}
      />

      <ModelFallbackSection
        value={fallback}
        onChange={setFallback}
        hint="Usado se o modelo principal falhar (provider fora, mídia que ele não conseguiu baixar). O passo fica marcado como fallback."
      />

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          style={{ padding: "8px 18px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}
        >
          {mut.isPending ? "Salvando..." : "Salvar"}
        </button>
        {saved && <span style={{ fontSize: 12, color: "var(--ok)" }}>✓ Salvo</span>}
      </div>
    </div>
  );
}
